/**
 * useTrialLock — React hook that fetches the current user's trial status
 * once and exposes:
 *   { onTrial, daysRemaining, expired, ready }
 *
 * Used by every list/detail component to decide whether to render
 * <LockedField> blur overlays + disable export buttons.
 *
 * The backend already masks the response payloads (search-leads, kanban,
 * saved-leads, public-works/opportunities) so the data is safe even if the
 * UI gate is bypassed. This hook just controls VISUAL affordances.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let _cache = null;       // module-level cache to avoid N concurrent fetches
let _cacheAt = 0;
const CACHE_MS = 60 * 1000;

export function useTrialLock() {
  const [state, setState] = useState({
    onTrial: false,
    daysRemaining: null,
    expired: false,
    ready: false,
  });

  const fetchStatus = useCallback(async () => {
    // Anonymous visitors (public landing/legal/SEO pages) have no trial —
    // skip the call entirely to avoid a guaranteed 401 in the console.
    let token = "";
    try { token = window.localStorage.getItem("alp.access_token") || ""; } catch (_e) { /* ignore */ }
    if (!token) {
      setState({ onTrial: false, daysRemaining: null, expired: false, ready: true });
      return;
    }
    if (_cache && Date.now() - _cacheAt < CACHE_MS) {
      setState({ ...(_cache), ready: true });
      return;
    }
    try {
      const { data } = await axios.get(`${API}/trial/status`);
      const onTrial = data.started && !data.is_admin && !data.expired;
      _cache = {
        onTrial,
        daysRemaining: data.days_remaining ?? null,
        expired: !!data.expired,
      };
      _cacheAt = Date.now();
      setState({ ..._cache, ready: true });
    } catch (_e) {
      // Anonymous / not logged in — treat as NOT on trial (no gating until login)
      _cache = { onTrial: false, daysRemaining: null, expired: false };
      _cacheAt = Date.now();
      setState({ ..._cache, ready: true });
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return { ...state, refetch: () => { _cache = null; fetchStatus(); } };
}
